import type { LucideIcon } from "lucide-react";
import { AlertTriangle, Inbox, Clock } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

export type EmptyStateVariant = "illustrated" | "typographic";
export type EmptyStateIntent = "empty" | "error" | "coming-soon";

export interface EmptyStateProps {
  variant?: EmptyStateVariant;
  /**
   * Visual tone of the empty state. `empty` is the default muted circle
   * used for "no rows" states; `error` flips the icon to red for failure
   * scenarios; `coming-soon` swaps the solid disc for a dashed outline so
   * stubbed sections read as "future work" rather than a blocking problem.
   */
  intent?: EmptyStateIntent;
  /** Defaults depend on `intent`. Rendered at 1.5px stroke per design system rules. */
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}

const intentIcon: Record<EmptyStateIntent, LucideIcon> = {
  empty: Inbox,
  error: AlertTriangle,
  "coming-soon": Clock,
};

const intentCircleClass: Record<EmptyStateIntent, string> = {
  empty: "bg-[var(--surface-2)]",
  error: "bg-[var(--neg-bg)]",
  "coming-soon": "border-2 border-dashed border-[var(--line-2)]",
};

const intentIconClass: Record<EmptyStateIntent, string> = {
  empty: "text-[var(--ink-3)]",
  error: "text-[var(--neg)]",
  "coming-soon": "text-[var(--ink-3)]",
};

/**
 * Shown when a list, table, or section has no rows. The `illustrated` variant
 * anchors the eye with a circular icon; the `typographic` variant is used in
 * tighter contexts (filter-zero states) where we don't want a second focal
 * point. Pass `intent` to reskin the circle for error / coming-soon contexts.
 */
export function EmptyState({
  variant = "illustrated",
  intent = "empty",
  icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  const Icon = icon ?? intentIcon[intent];
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-4 py-16 text-center",
        className,
      )}
    >
      {variant === "illustrated" && (
        <div
          aria-hidden
          className={cn(
            "inline-flex h-16 w-16 items-center justify-center rounded-full",
            intentCircleClass[intent],
          )}
        >
          <Icon
            className={cn("h-7 w-7", intentIconClass[intent])}
            strokeWidth={1.5}
          />
        </div>
      )}
      <div className="flex max-w-md flex-col gap-1.5">
        <h3 className="font-display text-[15px] font-semibold leading-tight tracking-tight text-[var(--ink)]">
          {title}
        </h3>
        {description && (
          <p className="text-[13px] text-[var(--ink-3)]">{description}</p>
        )}
      </div>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
